// app/javascript/controllers/mover_survey_controller.js
import { Controller } from "@hotwired/stimulus";
import Sortable from "sortablejs";

export default class extends Controller {
  static values = {
    items: Array,
    units: Array,
    inventory: Object,
    roomIdCounter: Number,
    surveyData: Array,
  };

  static targets = [
    "roomsContainer",
    "selectedRooms",
    "itemsList",
    "searchInput",
    "roomCard",
    "totalWeight",
    "totalVolume",
    "metadataTotalWeight",
    "metadataTotalRooms",
    "metadataTotalVolume",
    "customItemName",
    "customItemQty",
    "customItemSize",
    "addressSelector",
    'measurementSelector'
  ];


  connect() {
    // Initialize Sortable if needed
    this.setupEventListeners();
    this.initDefaultItems(); // Prepare the flat list of items

    this.units = ["cu ft", "cu m"]; // Example units
    this.roomIdCounterValue = this.roomIdCounterValue || 0; // Initialize the counter if not already set
    this.inventoryValue = this.inventoryValue || {};

    this.initializeAddressSelector();

 
  }



  setupEventListeners() {
 
    this.element.addEventListener("click", (event) => {
      if (event.target.matches(".remove-room-button")) {
        this.removeRoom(event);
      }
    });

    //get addresses
    document.addEventListener(
      "addressesUpdated",
      this.handleAddressUpdate.bind(this)
    );

  }

  debugBackendData() {
    console.log("Survey Data from backend:", this.surveyDataValue);
    // If the survey data is not empty, let's log more details
    if (this.surveyDataValue && this.surveyDataValue.length > 0) {
      this.surveyDataValue.forEach((survey, index) => {
        console.log(`Survey ${index + 1}:`);
        console.log("  Address ID:", survey.address_id);
        console.log("  Survey Data:", survey.data);
      });
    }

  }

  uuidv4() {
    return "xxyxx-xyxyx".replace(
      /[xy]/g,
      function (c) {
        const r = (Math.random() * 16) | 0,
          v = c == "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      }
    );
  }

  initialLoad() {
    const selector = this.addressSelectorTarget;
    
    if (selector.options.length > 0) {
      // Check if there's a preselected option (e.g., from a previous page load)
      let selectedOption = selector.querySelector('option:checked') || selector.options[0];
      selectedOption.selected = true;

      const selectedAddressId = selectedOption.value;
      
      //console.log("Initial load for address:", selectedAddressId);
      this.loadAndDisplayAddress(selectedAddressId);
    } else {
      console.log("No addresses available for initial load");
      // Optionally, display a message to the user about no addresses
    }
  }

  

  loadAndDisplayAddress(addressId) {
    const inventoryData = this.inventoryValue[addressId];
    //console.log("Loading inventory data for address:", addressId);
    if (inventoryData && inventoryData.rooms && inventoryData.rooms.length > 0) {
      // Clear existing rooms
      this.clearRooms();
      
      // Display rooms for this address
      inventoryData.rooms.forEach(room => {
        this.displayRoom(room, addressId);
      });
      
      this.updateTotalMetadata();
    } else {
      //console.log("No room data for address:", addressId);
      // Optionally, display a message about no rooms for this address
    }
  }

  initializeAddressSelector() {
    const selector = this.addressSelectorTarget;
    selector.innerHTML = ''; // Clear existing options
  
    if (Object.keys(this.inventoryValue).length === 0 && this.surveyDataValue.length === 0) {
      const primaryOption = document.createElement("option");
      primaryOption.value = "primary";
      primaryOption.textContent = "Primary";
      selector.appendChild(primaryOption);
      this.inventoryValue.primary = { rooms: [] };
    } else {
      Object.entries(this.inventoryValue).forEach(([addressId, data]) => {
        if (addressId !== 'primary') {
          const option = document.createElement("option");
          option.value = addressId;
          option.textContent = `${data.address.address}, ${data.address.city}`;
          selector.appendChild(option);
        }
      });
    }
  
    if (selector.options.length > 0) {
      selector.selectedIndex = 0;
      this.addressSelected();
    }
    this.initialLoad();
  }

  handleAddressUpdate(event) {
    const addresses = event.detail;
    const selector = this.addressSelectorTarget;

    // If there are no addresses, ensure we have a 'primary' option
    if (addresses.length === 0) {
      if (!selector.querySelector('option[value="primary"]')) {
        const primaryOption = document.createElement("option");
        primaryOption.value = "primary";
        primaryOption.textContent = "Primary";
        selector.appendChild(primaryOption);
      }
      return;
    }
  
    // If we have addresses, remove the 'primary' option
    const primaryOption = selector.querySelector('option[value="primary"]');
    if (primaryOption) {
      // If we have inventory data for 'primary', transfer it to the first address
      if (this.inventoryValue.primary) {
        const firstAddressId = addresses[0].id.toString();
        this.inventoryValue[firstAddressId] = this.inventoryValue.primary;
        delete this.inventoryValue.primary;
      }
      primaryOption.remove();
    }
  
    // Update or add options for each address
    addresses.forEach((address) => {
      const addressId = address.id.toString();
      let option = selector.querySelector(`option[value="${addressId}"]`);
      
      if (!option) {
        option = document.createElement("option");
        option.value = addressId;
        selector.appendChild(option);
      }
      
      option.textContent = `${address.address}, ${address.city}`;
  
      // Initialize or update inventory for this address
      // Check if we have saved survey data for this address
      const savedSurveyData = this.surveyDataValue.find(survey => survey.address_id.toString() === addressId);

      // Initialize or update inventory for this address
      if (!this.inventoryValue[addressId]) {
        // If we have saved survey data and no existing inventory, use the saved data
        if (savedSurveyData && savedSurveyData.data) {
          this.inventoryValue = { 
            ...this.inventoryValue,
            [addressId]: {
               address: address,
               rooms: savedSurveyData.data.rooms || []
             }
           };
       
        } else {
          // Otherwise, initialize with empty rooms
          this.inventoryValue[addressId] = { address: address, rooms: [] };
        }
      } else {
        // Update the address information
        this.inventoryValue[addressId].address = address;
      }
      //console.log("Inventory after update:", this.inventoryValue);
    
    });
  
    // Remove options for addresses that no longer exist
    Array.from(selector.options).forEach(option => {
      if (option.value !== 'primary' && !addresses.some(addr => addr.id.toString() === option.value)) {
        option.remove();
        delete this.inventoryValue[option.value];
      }
    });
  
    // If this is the first address being added, select it
    if (selector.options.length === 1) {
      selector.selectedIndex = 0;
      this.addressSelected();
    }
  
    this.updateTotalMetadata();
    //console.log("Updated inventory value:", this.inventoryValue);
    this.initialLoad();
    //console.log("inital load")
  }



  updateAddressSelector(addresses) {
    const selector = this.addressSelectorTarget;
    selector.innerHTML = ''; // Clear existing options

    if (addresses.length === 0) {
      const primaryOption = document.createElement("option");
      primaryOption.value = "primary";
      primaryOption.textContent = "Primary";
      selector.appendChild(primaryOption);
      this.inventoryValue.primary = { rooms: [] };
    } else {
      addresses.forEach((address) => {
        const option = document.createElement("option");
        option.value = address.id.toString();
        option.textContent = `${address.address}, ${address.city}`;
        selector.appendChild(option);

        // Initialize inventory for this address if not exists
        if (!this.inventoryValue[address.id]) {
          const savedSurveyData = this.surveyDataValue.find(survey => survey.address_id.toString() === address.id.toString());
          this.inventoryValue[address.id] = {
            address: address,
            rooms: savedSurveyData ? (savedSurveyData.data.rooms || []) : []
          };
        }
      });
    }

    //console.log("Address selector updated with options:", selector.options.length);
  }

  disconnect() {
    document.removeEventListener(
      "addressesUpdated",
      this.handleAddressUpdate.bind(this)
    );
  }

 

  dispatchInventoryUpdated(totalRooms, totalWeight, totalVolume, inventories) {
    const event = new CustomEvent("inventoryUpdated", {
      detail: {
        totalRooms: totalRooms,
        totalWeight: totalWeight,
        totalVolume: totalVolume,
        inventories: inventories
      },
      bubbles: true,
      cancelable: true,
    });
    //console.log("dispatchInventoryUpdated", event , {totalRooms, totalWeight, totalVolume, inventories});
    this.element.dispatchEvent(event);
  }

  clearRooms() {
    // Assuming `selectedRoomsTarget` is the container for room elements
    this.selectedRoomsTarget.innerHTML = ""; // Clears the displayed rooms
    //console.log("cleared rooms");
  }

  addressSelected() {
    const selectedId = this.addressSelectorTarget.value;
    //console.log("Address manually selected:", selectedId);
    this.loadAndDisplayAddress(selectedId);
  }

  loadInventory(selectedId) {
    this.clearRooms();
    const inventoryData = this.inventoryValue[selectedId] || { rooms: [] };
    this.displayInventory(inventoryData, selectedId);
  }

  displayInventory(inventoryData, addressId) {
    this.clearRooms();
    inventoryData.rooms.forEach((room) => this.displayRoom(room, addressId));
    this.updateTotalMetadata();
  }

  displaySavedInventory(savedData, addressId) {
    this.clearRooms();

    savedData.rooms.forEach(room => {
      const roomData = {
        id: room.id,
        category: room.category,
        items: room.items
      };
      this.displayRoom(roomData, addressId, true);
    });

    this.updateTotalMetadata();
  }


  initSortable() {
    // Example initialization, adjust according to your specific needs
    new Sortable(this.selectedRoomsTarget, {
      group: "shared",
      onClone: (event) => this.handleClone(event),
    });
  }

  initDefaultItems() {
    const unit = this.element.dataset.measurementUnitValue || 'imperial'; // Get the unit from data attributes, defaulting to 'imperial'
    // Parse and process items while maintaining the structure
    const nestedItems = JSON.parse(this.element.dataset.inventoryItemsValue).items;
    this.items = nestedItems.map(category => ({
      ...category,  // Spread the existing category properties
      items: category.items.map(item => ({
        ...item,  // Spread the existing item properties
        volume: this.convertVolume(item.volume, unit),  // Convert the volume
        unit: unit === 'metric' ? 'cu m' : 'cu ft'  // Set the unit
      }))
    }));



    this.flatItems = nestedItems.reduce((acc, category) => {
      const convertedItems = category.items.map(item => {
        // Convert volume based on the unit
        return {
          ...item,
          volume: this.convertVolume(item.volume, unit),
          unit: unit === 'metric' ? 'cu m' : 'cu ft'
        };
      });
      return [...acc, ...convertedItems];
    }, []);

  }



  // Helper function to convert volume
  convertVolume(volume, unit) {
    return unit === 'metric' ? (volume * 0.0283168).toFixed(2) : volume;
  }


  updateItem(event) {
    const inputElement = event.target;

    const roomElement = inputElement.closest(".room-card");
    const roomId = roomElement.dataset.id;
    const addressId = this.addressSelectorTarget.value;

    const nameElements = roomElement.querySelectorAll('[name="id_name"]'); // Adjust selector if needed
    //console.log(nameElements)
    const qtyInputs = roomElement.querySelectorAll('[name="qty"]');
    const sizeInputs = roomElement.querySelectorAll('[name="volume"]');
    let roomItems = [];

    qtyInputs.forEach((input, index) => {
      const item = {
        name: nameElements[index].textContent,
        qty: parseInt(input.value, 10),
        size: parseFloat(sizeInputs[index].value) || 0,
        // Assuming you also store the item's unique identifier or any other necessary data
      };
      roomItems.push(item);
    });

    this.updateRoomData(addressId, roomId, roomItems);
    this.updateRoomTotals(roomElement)
    this.updateTotalMetadata();
  
  }


  updateRoomData(addressId, roomId, items) {
    // Ensure the address exists in the inventory
    //console.log('updatedroomdata', items)
    if (!this.inventoryValue[addressId]) {
      this.inventoryValue[addressId] = { rooms: [] };
    }

    // Clone the items to prevent reference issues
    const itemsClone = items.map((item) => ({ ...item }));

    // Find the index of the room or -1 if not found
    const roomIndex = this.inventoryValue[addressId].rooms.findIndex(
      (r) => r.id === roomId
    );

    if (roomIndex !== -1) {
      // Update existing room: create a completely new array for immutability
      const newRooms = this.inventoryValue[addressId].rooms.map(
        (room, index) => {
          if (index === roomIndex) {
            return { ...room, items: itemsClone };
          } else {
            return room;
          }
        }
      );
      this.inventoryValue = {
        ...this.inventoryValue,
        [addressId]: { rooms: newRooms },
      };
    } else {
      // Room does not exist, add a new one
      const existingRooms = this.inventoryValue[addressId]
        ? this.inventoryValue[addressId].rooms
        : [];
      const updatedRooms = [
        ...existingRooms,
        { id: roomId, items: itemsClone },
      ];

      this.inventoryValue = {
        ...this.inventoryValue,
        [addressId]: { rooms: updatedRooms },
      };
    }

    // Update the entire state to ensure Stimulus recognizes the change
    this.inventoryValue = { ...this.inventoryValue };
  }



  updateRoomTotals(roomElement) {
    let totalVolume = 0;
    let totalWeight = 0;
    const unitType = this.element.dataset.measurementUnitValue || 'imperial'; // Get the unit from data attributes, defaulting to 'imperial'
    const weightMultiplier = unitType === 'metric' ? 112.1978 : 7; // Example weight conversion factor



    const items = roomElement.querySelectorAll(".item");

    items.forEach((item) => {

      const qty = Number(item.querySelector('[name="qty"]').value);
      const volume = Number(item.querySelector('[name="volume"]').value);
      totalVolume += qty * volume;
      totalWeight += qty * volume * weightMultiplier;
    });

    // Adjusting text content according to the measurement unit
    if (unitType === 'metric') {
      roomElement.querySelector('[data-target="room.totalWeight"]').textContent = `${totalWeight.toFixed(2)} kg`;
      roomElement.querySelector('[data-target="room.totalVolume"]').textContent = `${totalVolume.toFixed(2)} cu m`;

    } else {
      roomElement.querySelector('[data-target="room.totalWeight"]').textContent = `${totalWeight.toFixed(2)} lbs`;
      roomElement.querySelector('[data-target="room.totalVolume"]').textContent = `${totalVolume.toFixed(2)} cu m`;

    }
  }

  updateTotalMetadata() {
    let totalVolume = 0;
    let totalWeight = 0;
    let totalRooms = 0;
    const unitType = this.element.dataset.measurementUnitValue || 'imperial';
  
    this.roomCardTargets.forEach((room) => {
      const volumeText = room.querySelector('[data-target="room.totalVolume"]').textContent;
      const weightText = room.querySelector('[data-target="room.totalWeight"]').textContent;
      const volume = parseFloat(volumeText.replace(/[^0-9.]/g, ""));
      const weight = parseFloat(weightText.replace(/[^0-9.]/g, ""));
  
      totalVolume += volume;
      totalWeight += weight;
      totalRooms++;
    });
  
    // Update DOM
    this.metadataTotalRoomsTarget.textContent = totalRooms;
    this.metadataTotalWeightTarget.textContent = `${totalWeight.toFixed(2)} ${unitType === 'metric' ? 'kg' : 'lbs'}`;
    this.metadataTotalVolumeTarget.textContent = `${totalVolume.toFixed(2)} ${unitType === 'metric' ? 'cu m' : 'cu ft'}`;
  
    const cleanedInventories = this.cleanInventoryData(this.inventoryValue);
    //console.log(cleanedInventories);
    this.dispatchInventoryUpdated(totalRooms, totalWeight, totalVolume, cleanedInventories);
  }

  cleanInventoryData(inventories) {
    const cleaned = {};
    Object.entries(inventories).forEach(([addressId, inventory]) => {
      if (addressId !== '0' && addressId !== 'primary') {
        const cleanedRooms = inventory.rooms.map(room => ({
          ...room,
          items: room.items.filter(item => item.qty > 0)
        })).filter(room => room.items.length > 0);

        if (cleanedRooms.length > 0) {
          cleaned[addressId] = {
            ...inventory,
            rooms: cleanedRooms
          };
        }
      }
    });
    return cleaned;
  }


  handleClone(event) {
    const clonedElement = event.clone;
    this.initializeRoomCard(clonedElement);
    this.selectedRoomsTarget.appendChild(clonedElement);
  }

  saveRoom({ addressId, roomData }) {
    // Retrieve the current rooms or start with an empty array
    const currentRooms = this.inventoryValue[addressId]
      ? this.inventoryValue[addressId].rooms
      : [];

    // Find index of existing room
    const existingRoomIndex = currentRooms.findIndex(
      (r) => r.id === roomData.id
    );

    let newRooms;
    if (existingRoomIndex !== -1) {
      // Replace the existing room by creating a new array
      newRooms = [
        ...currentRooms.slice(0, existingRoomIndex),
        roomData,
        ...currentRooms.slice(existingRoomIndex + 1),
      ];
    } else {
      // Add new room to the array
      newRooms = [...currentRooms, roomData];
    }

    // Assign the new rooms array back to the inventoryValue for the specific address
    this.inventoryValue = {
      ...this.inventoryValue,
      [addressId]: { rooms: newRooms },
    };
  
    //console.log("Updated inventory state:", this.inventoryValue);
  }

  //bug here, adds an extra address id to inventory when adding a room the first time.  
  addRoomFromButton(event) {
    const roomId = event.currentTarget.dataset.id
      .replace(/\s+/g, "-")
      .toLowerCase();
    const roomData = this.items.find(
      (room) => room.category.toLowerCase().replace(/\s+/g, "-") === roomId
    );

    if (!roomData) {
      console.error("Room data not found for ID:", roomId);
      return;
    }

    this.roomIdCounterValue++;

    const uniqueRoomId = `${roomData.category}-${this.roomIdCounterValue}`;
    //console.log('bug uniq id', uniqueRoomId)
    //console.log("Adding room with unique ID:", uniqueRoomId);
    roomData.id = uniqueRoomId; // Assign unique ID to room data

    this.displayRoom(roomData, this.addressSelectorTarget.value, false);
  }



  displayRoom(roomData, addressId) {
    const roomTemplate = document.getElementById("room-template").content.cloneNode(true);
    const roomElement = roomTemplate.querySelector(".room-card");
    roomElement.dataset.id = roomData.id;
    roomElement.querySelector("[data-target='room.roomName']").textContent = roomData.category;

    this.initializeRoomCard(roomElement, roomData.items);
    this.selectedRoomsTarget.appendChild(roomElement);

    this.saveRoom({
      addressId: addressId,
      roomData: roomData,
    });
  }

  initializeRoomCard(roomElement, items) {
    const itemsContainer = roomElement.querySelector(".items-list");
    if (!itemsContainer) {
      console.error("Items container not found in the room card.");
      return;
    }

    if (items && items.length > 0) {
      items.forEach((item) => {
        let itemHTML = document.getElementById("item-detail-template").innerHTML;
        itemHTML = itemHTML.replace("%NAME%", item.name);
        itemHTML = itemHTML.replace("%QTY%", item.qty);
        itemHTML = itemHTML.replace("%VOLUME%", item.size);

        const unitSelectHTML = this.units
          .map((unit) => `<option value="${unit}">${unit}</option>`)
          .join("");
        itemHTML = itemHTML.replace(
          '<select class="unit-select w-24" name="unit">',
          `<select class="unit-select w-24" name="unit">${unitSelectHTML}`
        );

        itemsContainer.insertAdjacentHTML("beforeend", itemHTML);
      });
    }

    this.updateRoomTotals(roomElement);
    this.updateTotalMetadata();
  }

  removeRoom(event) {
    event.preventDefault();
    if (confirm("Are you sure you want to delete this room?")) {
      const roomElement = event.target.closest(".room-card");
      const roomId = roomElement.dataset.id; // Assuming each room card has a data-id attribute
      const addressId = this.addressSelectorTarget.value;


      if (roomElement) {
        roomElement.remove(); // Remove the room element from the DOM

        // Remove the room from the inventory data
        let f_rooms = this.inventoryValue[addressId]
          ? this.inventoryValue[addressId].rooms.filter(
            (room) => room.id !== roomId
          ) : [];



        this.inventoryValue = {
          ...this.inventoryValue,
          [addressId]: { rooms: f_rooms },
        };

        // Update the inventory to trigger reactivity
        //this.inventoryValue = { ...this.inventoryValue };

        this.updateTotalMetadata(); // Update totals after removal
        //console.log("Room removed. Updated inventory:", this.inventoryValue);
      }
    }
  }

  filterItems(event) {
    const query = event.target.value.toLowerCase();
    // Assuming flatItems contains all items across all categories
    const filteredItems = this.flatItems.filter((item) =>
      item.name.toLowerCase().includes(query)
    );
    const searchResultsList = event.target.nextElementSibling; // Make sure this selects the correct ul for results

    // Clear previous results
    searchResultsList.innerHTML = "";

    // Add new results
    filteredItems.forEach((item) => {
      const itemElement = document.createElement("li");
      itemElement.className =
        "p-2 border border-gray-300 rounded hover:bg-gray-100 w-5/6 cursor-pointer";
      itemElement.textContent = item.name;
      itemElement.dataset.action = "click->mover-survey#addItemToRoom";
      itemElement.dataset.item = JSON.stringify(item); // Store item data for easy access when adding
      searchResultsList.appendChild(itemElement);
    });

    // Make sure to show the results list if hidden
    searchResultsList.classList.remove("hidden");
  }

  displayItems(items) {
    const itemsList = this.selectedRoomsTarget.querySelector(".items-list");
    itemsList.innerHTML = ""; // Clear existing entries
    items.forEach((item) => {
      const itemElement = document.createElement("li");

      itemElement.textContent = item.name;
      itemElement.dataset.id = item.id;
      itemElement.dataset.action = "click->inventory#addItem";
      itemsList.appendChild(itemElement);
    });
  }

  addItemToRoom(event) {
    const item = JSON.parse(event.target.dataset.item);
    const roomCard = event.target.closest(".room-card");
    const itemsList = roomCard.querySelector(".items-list");

    // Use the template to create new item entry
    let itemHTML = document.getElementById("item-detail-template").innerHTML;

    itemHTML = itemHTML.replace("%NAME%", item.name);
    itemHTML = itemHTML.replace("%QTY%", item.qty || "0"); // Default to '0' if qty not set
    itemHTML = itemHTML.replace("%VOLUME%", item.volume);
    itemHTML = itemHTML.replace("%UNIT%", this.units[0]); // Default to first unit type

    itemsList.insertAdjacentHTML("afterbegin", itemHTML);

    // Hide the search results
    event.target.parentElement.classList.add("hidden");
    const searchInput = roomCard.querySelector('input[type="text"]');
    if (searchInput) {
      searchInput.value = ""; // Clear the input field
    }
  }

  addCustomItemToRoom(event, itemName = null, itemQty = null, itemSize = null, roomElement = null) {
    if (event) event.preventDefault();
    
    if (!roomElement) {
      roomElement = event.target.closest(".room-card");
    }
    
    const itemsList = roomElement.querySelector(".items-list");

    const name = itemName || roomElement.querySelector('[data-mover-survey-target="customItemName"]').value;
    const qty = itemQty || parseInt(roomElement.querySelector('[data-mover-survey-target="customItemQty"]').value);
    const size = itemSize || parseFloat(roomElement.querySelector('[data-mover-survey-target="customItemSize"]').value);

    if (!name || qty <= 0 || size <= 0) {
      if (event) alert("Please fill in all fields correctly.");
      return;
    }

    let itemHTML = document.getElementById("item-detail-template").innerHTML;
    itemHTML = itemHTML.replace("%NAME%", name);
    itemHTML = itemHTML.replace("%QTY%", qty);
    itemHTML = itemHTML.replace("%VOLUME%", size);
    itemHTML = itemHTML.replace("%UNIT%", this.units[0]);

    itemsList.insertAdjacentHTML("afterbegin", itemHTML);

    if (event) {
      // Clear input fields after adding the item
      roomElement.querySelector('[data-mover-survey-target="customItemName"]').value = "";
      roomElement.querySelector('[data-mover-survey-target="customItemQty"]').value = "";
      roomElement.querySelector('[data-mover-survey-target="customItemSize"]').value = "";
    }

    this.updateRoomTotals(roomElement);
    this.updateTotalMetadata();
  }

  removeItemFromRoom(event) {
    event.preventDefault(); // Prevent default button click behavior
    const itemElement = event.target.closest("li"); // Find the <li> parent of the button
    if (itemElement) {
      itemElement.remove(); // Remove the item element from the DOM
    }
  }
}
